import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Search, Lock, RefreshCw, Pause, Play,
  Home, Coffee, AlertCircle, CheckCircle, X, Square,
  ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import { useManufacturingContext } from '../../contexts/ManufacturingContext';
import '../../components/styles/Manufacturing.css';
import { getAvailableWorkers } from '../../services/manufacturing';
import { API_URL } from '../../config/api';
import TransferButton from '../buttons/TransferButton';
import ReportButton from '../buttons/roomReportButton';

// Ticks on its own so the clock doesn't force the whole page (table, sorting,
// due-date math) to re-render every second.
const LiveClock = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="time-display">
      <div>{currentTime.toLocaleDateString()}</div>
      <div>{currentTime.toLocaleTimeString()}</div>
    </div>
  );
};

const TabletManufacturingPage = () => {
  const { roomNo } = useParams();
  const [pinCode, setPinCode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [workerFilter, setWorkerFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'scanInDate', direction: 'desc' });
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [taskSlideIndex, setTaskSlideIndex] = useState(null); // null = closed, number = open at index
  const slideswipeX = React.useRef(null);
  


// Get everything from context
const {
  // State
  isAuthenticated,
  user,
  userJobTitle,
  currentRoom,
  roomInventory,
  selectedTask,
  loading,
  error,
  success,
  holdModal,
  
  // ADD THESE MISSING PROPERTIES:
  searchResults,
  isShowingSearchResults,
  lastSearchTerm,
  
  // Actions
  authenticate,
  logout,
  setCurrentRoom,
  findBarcode,
  startWork,
  finishWork,
  holdTask,
  resumeTask,
  selectTask,
  clearSelectedTask,
  clearMessages,
  openHoldModal,
  closeHoldModal,
  
  // ADD THIS MISSING ACTION:
  clearSearchAndShowAll,
  
  // Computed properties
  canStartWork,
  canFinishWork,
  canPause,
  canResume,
  isTaskSelected,
} = useManufacturingContext();
  // Set room number from URL params
  useEffect(() => {
    if (roomNo && roomNo !== currentRoom) {
      setCurrentRoom(roomNo);
      setPinCode(roomNo);
    }
  }, [roomNo, currentRoom, setCurrentRoom]);

  // Load available workers when room is set
useEffect(() => {
  const loadWorkers = async () => {
    if (currentRoom && isAuthenticated) {
      try {
        const workers = await getAvailableWorkers(currentRoom);
        setAvailableWorkers(workers);
      } catch (error) {
        console.error('Failed to load workers:', error);
        setAvailableWorkers([]);
      }
    }
  };
  
  loadWorkers();
}, [currentRoom, isAuthenticated]);

  // Clear worker selection when task changes
  useEffect(() => {
    setSelectedWorker(null);
  }, [selectedTask]);

  // Fetch media attachments when a task is selected
  useEffect(() => {
    if (!selectedTask?.orderNo) {
      setTaskAttachments([]);
      return;
    }
    fetch(`${API_URL}/api/media/${selectedTask.orderNo}`)
      .then(r => r.json())
      .then(data => setTaskAttachments(data.attachments || []))
      .catch(() => setTaskAttachments([]));
  }, [selectedTask?.orderNo]);

  // Keyboard navigation for slideshow
  useEffect(() => {
    if (taskSlideIndex === null) return;
    const sorted = [...taskAttachments].sort((a, b) =>
      (b.TaskID === selectedTask?.id ? 1 : 0) - (a.TaskID === selectedTask?.id ? 1 : 0)
    );
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') setTaskSlideIndex(i => (i + 1) % sorted.length);
      if (e.key === 'ArrowLeft')  setTaskSlideIndex(i => (i - 1 + sorted.length) % sorted.length);
      if (e.key === 'Escape')     setTaskSlideIndex(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [taskSlideIndex, taskAttachments, selectedTask?.id]);

  // Handle PIN submission
  const handlePinSubmit = async () => {
    if (pinCode.length >= 3) {
      if (pinCode !== currentRoom) {
        clearMessages();
        setTimeout(() => {
          alert(`PIN must match room number (${currentRoom})`);
        }, 100);
        return;
      }

      try {
        await authenticate(pinCode);
        setPinCode('');
      } catch (error) {
        // Error handled by context
      }
    }
  };

  // Handle barcode search
const handleBarcodeSearch = async () => {
  if (!barcode.trim()) return;
  
  try {
    await findBarcode(barcode.trim());
    setBarcode('');
  } catch (error) {
    // Error handled by context
  }
};

  // Handle hold task with reason
  const handleHoldSubmit = async () => {
    if (!holdReason.trim()) {
      setHoldReason('Please Enter Reason');
      return;
    }
    
    try {
      await holdTask(holdReason, holdModal.taskId);
      closeHoldModal();
      setHoldReason('');
    } catch (error) {
      // Error handled by context
    }
  };

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
    // Toggle the light-theme class on the root element
    if (isDarkTheme) {
      document.documentElement.classList.add('light-theme');
    } else {
      document.documentElement.classList.remove('light-theme');
    }
  };

  // Add useEffect to sync with existing theme on mount
  useEffect(() => {
    const hasLightTheme = document.documentElement.classList.contains('light-theme');
    setIsDarkTheme(!hasLightTheme);
  }, []);

  // Handle start work with worker selection
  const handleStartWork = async () => {
    if (!selectedWorker) {
      alert('Please select a worker first');
      return;
    }
    
    try {
      // Pass both taskId and workerId to startWork
      await startWork(selectedTask?.id, selectedWorker.id);
      setSelectedWorker(null);
    } catch (error) {
      // Error handled by context
    }
  };


  // Get status badge class
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'in-process':
      case 'in progress':
        return 'status-badge status-in-process';
      case 'task completed':
      case 'completed':
        return 'status-badge status-completed';
      case 'on hold':
      case 'task on hold':
        return 'status-badge status-on-hold';
      case 'lunch break':
        return 'status-badge status-lunch-break';
      case 'not started':
      case 'task reset':
        return 'status-badge status-not-started';
      default:
        return 'status-badge status-not-started';
    }
  };

  // Format time
  const formatTime = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return '';
    }
  };

  // Check if due date is urgent and return appropriate color
  const getDueDateUrgency = (dueDate, status) => {
    if (!dueDate || (currentRoom !== '3002' && currentRoom !== '2002')) return null;

    // Ignore completed tasks
    if (status === 'Task Completed' || status === 'completed') return null;

    try {
      const due = new Date(dueDate);
      due.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

      const diffTime = due - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Past due or due today - RED
      if (diffDays <= 0) {
        return 'rgba(220, 38, 38, 0.7)'; // Red
      }

      // Less than 5 days - ORANGE
      if (diffDays < 5) {
        return 'rgba(249, 115, 22, 0.6)'; // Orange
      }

      return null;
    } catch {
      return null;
    }
  };

  // Add this function near your other utility functions
const formatScanTime = (date) => {
  if (!date) return 'Not scanned';
  try {
    // Since we're storing local time as a string, we need to treat it as local time
    // not UTC. Remove any timezone indicators and parse as local.
    let dateStr = date.toString().replace('T', ' ').replace('Z', '').replace('.000', '');
    
    // Create date by parsing the components manually to avoid timezone conversion
    const parts = dateStr.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (parts) {
      const d = new Date(
        parseInt(parts[1]), // year
        parseInt(parts[2]) - 1, // month (0-based)
        parseInt(parts[3]), // day
        parseInt(parts[4]), // hour
        parseInt(parts[5]), // minute
        // parseInt(parts[6])  // second
      );
      
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        // second: '2-digit',
        hour12: true
      });
    }
    
    return 'Invalid date';
  } catch {
    return 'Invalid date';
  }
};

  // UploadedDate comes from SQL Server's GETDATE(), which runs on the DB
  // server's clock (UTC) - unlike formatScanTime's column, this is a real
  // UTC instant and needs an actual timezone conversion, not raw digits.
  const formatUploadDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Workers assigned to this room, for the filter dropdown
  const workerOptions = useMemo(() => {
    return [...availableWorkers].sort((a, b) => a.name.localeCompare(b.name));
  }, [availableWorkers]);

  // Unique list of order warehouses present in the current data set, for the filter dropdown
  const warehouseOptions = useMemo(() => {
    const dataToSort = isShowingSearchResults ? searchResults : roomInventory;
    if (!dataToSort) return [];
    const warehouses = new Set(
      dataToSort
        .map(item => item.orderWarehouse)
        .filter(Boolean)
    );
    return [...warehouses].sort();
  }, [roomInventory, searchResults, isShowingSearchResults]);

  // Human-readable summary of active filters, shown in the generated report
  const activeFilterLabel = useMemo(() => {
    const parts = [];
    if (workerFilter) parts.push(`Worker: ${workerFilter}`);
    if (warehouseFilter) parts.push(`Warehouse: ${warehouseFilter}`);
    return parts.join(', ');
  }, [workerFilter, warehouseFilter]);

  // Sort the inventory data
  const sortedInventory = useMemo(() => {
    const dataToSort = isShowingSearchResults ? searchResults : roomInventory;
    if (!dataToSort) return [];

    const filteredByWorker = workerFilter
      ? dataToSort.filter(item => item.worker === workerFilter)
      : dataToSort;

    const filteredByWarehouse = warehouseFilter
      ? filteredByWorker.filter(item => item.orderWarehouse === warehouseFilter)
      : filteredByWorker;

    const sorted = [...filteredByWarehouse].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Handle date sorting
      if (sortConfig.key === 'dueDate' || sortConfig.key === 'scanInDate' || sortConfig.key === 'scanOutDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle number sorting
      if (sortConfig.key === 'orderNo' || sortConfig.key === 'id') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // String comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [roomInventory, searchResults, isShowingSearchResults, sortConfig, workerFilter, warehouseFilter]);

  // Get sort icon for column header
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} style={{ opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={14} />
      : <ArrowDown size={14} />;
  };

  // Check if start button should be enabled
  const canStartWorkWithWorker = canStartWork && selectedWorker;

  // Authentication Screen
  if (!isAuthenticated) {
    return (
      <div className="manufacturing-container">
        <div className="manufacturing-header">
          <div className="header-content">
            <div className="logo">
              <img src="/images/dini_logo.png" alt="DiNi Wigs" style={{ height: '50px', width: 'auto' }} />
            </div>
             {/* Add Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle">
              <span className="theme-icon">
                {isDarkTheme ? '🌙' : '☀️'}
              </span>
              {isDarkTheme ? 'Dark' : 'Light'}
            </button>
            <LiveClock />
          </div>
        </div>

        <div className="auth-screen">
          <h1 className="room-title">Room {currentRoom}</h1>
          <p className="auth-hint" style={{ marginBottom: '28px' }}>Enter PIN to access room</p>

          <div className="auth-card">
            <h2>Authentication Required</h2>
            <p className="auth-hint">Enter PIN Code for Room {currentRoom}</p>
            <div className="auth-row">
              <input
                type="password"
                placeholder={`Enter PIN (${currentRoom})`}
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                className="search-input"
                style={{ maxWidth: '200px' }}
                disabled={loading}
                autoFocus
              />
              <button
                onClick={handlePinSubmit}
                disabled={loading || pinCode.length < 3}
                className="traffic-btn btn-search"
              >
                {loading ? 'Verifying...' : 'Login'}
              </button>
            </div>
            <p className="auth-hint">Hint: PIN matches the room number</p>
            {error && (
              <div className="message message-error">
                <div className="message-content">
                  <AlertCircle className="message-icon" />
                  {error}
                </div>
              </div>
            )}
          </div>

          {/* <a
            href="/employee-manual"
            className="em-launch-btn"
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <i className="fas fa-book-open" />
            Employee Manual
          </a> */}
        </div>
      </div>
    );
  }

  // Main Manufacturing Interface
  return (
    <div className="manufacturing-container">
      {/* Header */}
      <div className="manufacturing-header">
        
        <div className="header-content">
          <div className="logo">
            
            <img 
                src="/images/dini_title.png" 
                style={{ height: '80px', width: 'auto' }}
                alt="DiNI Wigs Logo"
              />
          </div>
          <button onClick={toggleTheme} className="theme-toggle">
              <span className="theme-icon">
                {isDarkTheme ? '🌙' : '☀️'}
              </span>
              {isDarkTheme }
            </button>
          <LiveClock />
        </div>
      </div>

      <div className="main-content">
        {/* Title and Welcome */}
        <div className="page-title-manu">
          <h1 className="room-title">Room {currentRoom} - Manufacturing</h1>
         {/* Secondary Controls */}
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', gap: '12px', flexWrap: 'wrap'}}>
            <div style={{display: 'flex', gap: '12px'}}>
              {canResume && (
                <button 
                  onClick={() => resumeTask()}
                  disabled={loading}
                  className="traffic-btn btn-start"
                  style={{minWidth: 'auto', padding: '12px 16px', fontSize: '16px'}}
                >
                  <Play className="btn-icon" />
                  Resume
                </button>
              )}
                <ReportButton
                  roomNo={currentRoom}
                  disabled={loading}
                  sortedData={sortedInventory}
                  activeFilters={activeFilterLabel}
                />
              
              {/* Transfer Button - only show when task is completed */}
              {selectedTask?.status === 'Task Completed' && selectedTask?.orderNo && (
                <TransferButton 
                  orderNo={selectedTask.orderNo}
                  variant="success"
                  size="md"
                  showText={true}
                  className="traffic-btn"
                  disabled={loading}
                />
              )}
            </div>

            <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', color: 'var(--text-secondary)'}}>
              <select
                value={workerFilter}
                onChange={(e) => setWorkerFilter(e.target.value)}
                disabled={loading}
                className="traffic-btn btn-secondary"
                style={{minWidth: 'auto', padding: '12px 16px', fontSize: '14px', color: 'var(--text-primary)'}}
              >
                <option value="" style={{color: 'var(--text-primary)', backgroundColor: 'var(--mfg-dark-elevated)'}}>All Workers</option>
                {workerOptions.map((worker) => (
                  <option key={worker.id} value={worker.name} style={{color: 'var(--text-primary)', backgroundColor: 'var(--mfg-dark-elevated)'}}>{worker.name}</option>
                ))}
              </select>
              <select
                value={warehouseFilter}
                onChange={(e) => setWarehouseFilter(e.target.value)}
                disabled={loading}
                className="traffic-btn btn-secondary"
                style={{minWidth: 'auto', padding: '12px 16px', fontSize: '14px', color: 'var(--text-primary)'}}
              >
                <option value="" style={{color: 'var(--text-primary)', backgroundColor: 'var(--mfg-dark-elevated)'}}>All Warehouses</option>
                {warehouseOptions.map((warehouse) => (
                  <option key={warehouse} value={warehouse} style={{color: 'var(--text-primary)', backgroundColor: 'var(--mfg-dark-elevated)'}}>{warehouse}</option>
                ))}
              </select>
              <button
                onClick={logout}
                disabled={loading}
                className="traffic-btn btn-finish"
                style={{minWidth: 'auto', padding: '12px 16px', fontSize: '16px'}}
              >
                <Lock className="btn-icon" />
                Lock
              </button>
            </div>
          </div>
        </div>
          <div className="inventory-count-bar">
            {isShowingSearchResults
              ? `Search Results: ${sortedInventory.length} items${lastSearchTerm ? ` for "${lastSearchTerm}"` : ''}`
              : `Total Wigs: ${new Set(sortedInventory.map(item => item.barcode)).size} (${sortedInventory.length} tasks)`
            }
            {workerFilter && ` · Worker: ${workerFilter}`}
            {warehouseFilter && ` · Warehouse: ${warehouseFilter}`}
          </div>

          {(currentRoom === '3002' || currentRoom === '2002') && (
            <div style={{display: 'flex', alignItems: 'center', gap: '20px', margin: '0 16px 16px', fontSize: '14px', color: 'var(--mfg-text-secondary)'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span style={{width: '14px', height: '14px', borderRadius: '3px', background: 'rgba(220, 38, 38, 0.7)', display: 'inline-block'}} />
                Due today or overdue
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span style={{width: '14px', height: '14px', borderRadius: '3px', background: 'rgba(249, 115, 22, 0.6)', display: 'inline-block'}} />
                Due within 5 days
              </div>
            </div>
          )}

        {/* Messages */}
        {error && (
          <div className="message message-error">
            <div className="message-content">
              <AlertCircle className="message-icon" />
              {error}
            </div>
            <button onClick={clearMessages} className="message-close">
              <X style={{width: '20px', height: '20px'}} />
            </button>
          </div>
        )}
        
        {success && (
          <div className="message message-success">
            <div className="message-content">
              <CheckCircle className="message-icon" />
              {success}
            </div>
            <button onClick={clearMessages} className="message-close">
              <X style={{width: '20px', height: '20px'}} />
            </button>
          </div>
        )}

                {/* Control Bar */}
        <div className="control-bar">
          <div className="control-row">
            {/* Search Section */}
            <div className="search-section">
              <div className="search-input-group">
                <label className="search-label">Scan Barcode:</label>
                <input
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleBarcodeSearch()}
                  className="search-input"
                  placeholder="Enter or scan barcode"
                  disabled={loading}
                />
              </div>
              <button
                onClick={handleBarcodeSearch}
                disabled={loading || !barcode.trim()}
                className="traffic-btn btn-search"
              >
                <Search className="btn-icon" />
                {loading ? 'Searching...' : 'Find'}

              </button>
              {isShowingSearchResults && (
                <button
                  onClick={clearSearchAndShowAll}
                  disabled={loading}
                  className="traffic-btn btn-secondary"
                >
                  <X className="btn-icon" />
                  Show All
                </button>
              )}
            </div>

            {/* Traffic Light Controls */}
            <div className="traffic-controls">
              <button 
                onClick={handleStartWork}
                disabled={loading || !canStartWork || !selectedWorker}
                className="traffic-btn btn-start"
              >
                <Play className="btn-icon" />
                {!selectedWorker ? 'Select Worker First' : 'Start'}
              </button>
              
              <button 
                onClick={() => openHoldModal(selectedTask?.id)}
                disabled={loading || !canPause}
                className="traffic-btn btn-pause"
              >
                <Pause className="btn-icon" />
                Pause
              </button>
              
              <button 
                  onClick={() => finishWork()}  
                disabled={loading || !canFinishWork}
                className="traffic-btn btn-finish"
              >
                <Square className="btn-icon" />
                Finish
              </button>
            </div>
          </div>

       
        </div>

      {/* Updated Inventory Table - Remove Hold Details Column */}
        <div className="inventory-container">         
          <div className="inventory-table-container">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('orderNo')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Order No {getSortIcon('orderNo')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('barcode')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      WigCode {getSortIcon('barcode')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('roomTask')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Task {getSortIcon('roomTask')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('scanInDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Start Date {getSortIcon('scanInDate')}
                      {/* make this the start date  */}
                    </div>
                  </th>
                  {(currentRoom === '3002' || currentRoom === '2002') && (
                    <th onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Due Date {getSortIcon('dueDate')}
                      </div>
                    </th>
                  )}
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Status {getSortIcon('status')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('worker')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Worker {getSortIcon('worker')}
                    </div>
                  </th>
                  <th onClick={() => handleSort('customer')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      Customer {getSortIcon('customer')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedInventory.map((item, index) => {
                  const urgencyColor = getDueDateUrgency(item.dueDate, item.status);
                  return (
                    <tr
                      key={`task-${item.id}-${index}`}
                      className={selectedTask?.id == item.id ? 'selected' : ''}
                      onClick={() => selectTask(item)}
                      style={urgencyColor ? { backgroundColor: urgencyColor } : {}}
                    >
                    <td className="order-number">
                      <a
                        href={`/work-order-profile/${item.orderNo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {item.orderNo}
                      </a>
                    </td>
                    <td><span className="barcode">{item.barcode}</span></td>
                    <td>{item.roomTask}</td>
                    <td>{formatScanTime(item.scanInDate)}</td>
                    {(currentRoom === '3002' || currentRoom === '2002') && (
                      <td>{formatTime(item.dueDate)}</td>
                    )}
                    <td>
                      <span className={getStatusClass(item.status)}>
                        {item.status}
                      </span>
                    </td>
                    <td>{item.worker || 'Select Worker'}</td>
                    <td>{item.customer}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Worker Selection (only show when task is selected and can start work) */}
        {isTaskSelected && !['In-Process', 'Task Completed'].includes(selectedTask?.status) && (
          <div className="worker-selection">
            <h3 className="worker-section-title">Select Worker to Start Task</h3>
            <div className="worker-grid">
              {availableWorkers.map((worker) => (
                <button
                  key={worker.id}
                  onClick={() => setSelectedWorker(worker)}
                  className={`worker-btn ${selectedWorker?.id === worker.id ? 'selected' : ''}`}
                >
                  {worker.name}
                </button>
              ))}
              {availableWorkers.length === 0 && (
                <div style={{gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280', padding: '20px'}}>
                  No workers available for this room
                </div>
              )}
            </div>
          </div>
        )}

        {/* Task Details */}
        <div className="task-details">
          <h3 className="task-details-title">Selected Task Details</h3>
          {selectedTask ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
              
              {/* Hold Details - Show prominently if task is paused */}
              {selectedTask.isWorkPaused && (
                <div className="hold-banner">
                  <div className="hold-banner-title">⏸ TASK ON HOLD</div>
                  <div className="hold-banner-grid">
                    <div>
                      <div className="hold-detail-label">Hold Reason</div>
                      <div className="hold-detail-value">{selectedTask.holdReason}</div>
                    </div>
                    <div>
                      <div className="hold-detail-label">Paused By</div>
                      <div className="hold-detail-value">{selectedTask.onHoldBy}</div>
                    </div>
                    <div>
                      <div className="hold-detail-label">Since</div>
                      <div className="hold-detail-value">{formatScanTime(selectedTask.onHoldStartDate)}</div>
                    </div>
                    {selectedTask.holdHours && (
                      <div>
                        <div className="hold-detail-label">Hold Duration</div>
                        <div className="hold-detail-value">{selectedTask.holdHours} hours</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scan Times */}
              <div className="scan-times-panel">
                <div className="scan-time-block">
                  <div className="scan-time-label">Scan In Time</div>
                  <div className={`scan-time-value ${selectedTask.scanInDate ? 'scanned-in' : 'empty'}`}>
                    {formatScanTime(selectedTask.scanInDate)}
                  </div>
                  {selectedTask.scanInBy && (
                    <div className="scan-time-by">by {selectedTask.scanInBy}</div>
                  )}
                </div>
                <div className="scan-time-block">
                  <div className="scan-time-label">Scan Out Time</div>
                  <div className={`scan-time-value ${selectedTask.scanOutDate ? 'scanned-out' : 'empty'}`}>
                    {formatScanTime(selectedTask.scanOutDate)}
                  </div>
                  {selectedTask.scanOutBy && (
                    <div className="scan-time-by">by {selectedTask.scanOutBy}</div>
                  )}
                </div>
              </div>

              {/* Task Info Grid */}
              <div className="task-info-grid-bg">
                <div className="task-info-item">
                  <div className="task-info-label">Task ID</div>
                  <div className="task-info-value">{selectedTask.id}</div>
                </div>
                
                <div className="task-info-item">
                  <div className="task-info-label">Order Number</div>
                  <div className="task-info-value">{selectedTask.orderNo}</div>
                </div>
                
                <div className="task-info-item">
                  <div className="task-info-label">Barcode</div>
                  <div className="task-info-value barcode">{selectedTask.barcode}</div>
                </div>
                
                <div className="task-info-item">
                  <div className="task-info-label">Status</div>
                  <div>
                    <span className={getStatusClass(selectedTask.status)}>
                      {selectedTask.status}
                    </span>
                  </div>
                </div>
                
                <div className="task-info-item">
                  <div className="task-info-label">Customer</div>
                  <div className="task-info-value customer">{selectedTask.customer}</div>
                </div>
                
                <div className="task-info-item">
                  <div className="task-info-label">Due Date</div>
                  <div className="task-info-value due-date">{formatTime(selectedTask.dueDate)}</div>
                </div>
                
                <div className="task-info-item">
                  <div className="task-info-label">Room Task</div>
                  <div className="task-info-value">{selectedTask.roomTask}</div>
                </div>
                
                {selectedTask.worker && selectedTask.worker !== 'Select Worker' && (
                  <div className="task-info-item">
                    <div className="task-info-label">Assigned Worker</div>
                    <div className="task-info-value">{selectedTask.worker}</div>
                  </div>
                )}
              </div>

              {/* Task Notes - Full Width if Present */}
                {selectedTask.taskNotes && (
                  <div className="notes-box task-notes-box">
                    <div className="notes-section-header task-notes">Task Notes</div>
                    <div className="notes-section-content">{selectedTask.taskNotes}</div>
                  </div>
                )}

                {selectedTask.qcNotes && (
                  <div className="notes-box qc-notes-box">
                    <div className="notes-section-header qc-notes">QC Notes</div>
                    <div className="notes-section-content">{selectedTask.qcNotes}</div>
                  </div>
                )}

                {/* Media Attachments - slideshow */}
                {taskAttachments.length > 0 && (() => {
                  const sorted = [...taskAttachments].sort((a, b) =>
                    (b.TaskID === selectedTask?.id ? 1 : 0) - (a.TaskID === selectedTask?.id ? 1 : 0)
                  );
                  const taskCount = sorted.filter(a => a.TaskID === selectedTask?.id).length;
                  return (
                    <div className="room-media-section">
                      <div className="room-media-header">
                        📎 MEDIA ({sorted.length})
                        {taskCount > 0 && <span className="room-media-badge">{taskCount} this task</span>}
                      </div>
                      <div className="room-media-grid">
                        {sorted.map((att, idx) => {
                          const isTaskSpecific = att.TaskID === selectedTask?.id;
                          return (
                            <div
                              key={att.ID}
                              className={`room-media-thumb${isTaskSpecific ? ' task-specific' : ''}`}
                              onClick={() => setTaskSlideIndex(idx)}
                            >
                              {att.MediaType === 'image' ? (
                                <img src={att.MediaURL} alt={att.Caption || att.FileName} />
                              ) : (
                                <div className="video-thumb">▶️</div>
                              )}
                              {att.Caption && <div className="thumb-caption">{att.Caption}</div>}
                              <div className="thumb-date">{formatUploadDate(att.UploadedDate)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

            </div>
          ) : (
            <div className="no-task-selected">
              <AlertCircle className="no-task-icon" />
              <p style={{fontSize: '18px', marginBottom: '8px'}}>No task selected</p>
              <p style={{fontSize: '16px', opacity: '0.7'}}>Click on a specific task row above or scan a barcode</p>
            </div>
          )}
        </div>

        {/* Hold Reason Modal */}
        {holdModal.isOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3 className="modal-title">Hold Reason</h3>
              <input
                type="text"
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleHoldSubmit()}
                className="modal-input"
                placeholder="Enter hold reason"
                autoFocus
              />
              <div className="modal-buttons">
                <button
                  onClick={handleHoldSubmit}
                  disabled={!holdReason.trim() || loading}
                  className="modal-btn modal-btn-primary"
                >
                  Submit
                </button>
                <button
                  onClick={() => {
                    closeHoldModal();
                    setHoldReason('');
                  }}
                  disabled={loading}
                  className="modal-btn modal-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Media slideshow overlay */}
        {taskSlideIndex !== null && (() => {
          const sorted = [...taskAttachments].sort((a, b) =>
            (b.TaskID === selectedTask?.id ? 1 : 0) - (a.TaskID === selectedTask?.id ? 1 : 0)
          );
          const att = sorted[taskSlideIndex];
          const isTaskSpecific = att?.TaskID === selectedTask?.id;
          return (
            <div
                className="room-slideshow-overlay"
                onClick={(e) => { if (e.target === e.currentTarget) setTaskSlideIndex(null); }}
                onTouchStart={e => { slideswipeX.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                  if (slideswipeX.current === null) return;
                  const dx = e.changedTouches[0].clientX - slideswipeX.current;
                  slideswipeX.current = null;
                  if (Math.abs(dx) > 50) {
                    dx < 0
                      ? setTaskSlideIndex(i => (i + 1) % sorted.length)
                      : setTaskSlideIndex(i => (i - 1 + sorted.length) % sorted.length);
                  }
                }}
              >
              <button className="room-slideshow-close" onPointerDown={e => { e.stopPropagation(); setTaskSlideIndex(null); }}>&times;</button>

              {sorted.length > 1 && (
                <button className="room-slideshow-nav prev" onPointerDown={e => { e.stopPropagation(); setTaskSlideIndex(i => (i - 1 + sorted.length) % sorted.length); }}>&#8249;</button>
              )}

              {att.MediaType === 'image' ? (
                <img src={att.MediaURL} alt={att.Caption || att.FileName} className="room-slideshow-media" onClick={e => e.stopPropagation()} />
              ) : (
                <video src={att.MediaURL} controls autoPlay className="room-slideshow-video" onClick={e => e.stopPropagation()} />
              )}

              {sorted.length > 1 && (
                <button className="room-slideshow-nav next" onPointerDown={e => { e.stopPropagation(); setTaskSlideIndex(i => (i + 1) % sorted.length); }}>&#8250;</button>
              )}

              <div className="room-slideshow-footer" onPointerDown={e => e.stopPropagation()}>
                {isTaskSpecific && <span className="room-slideshow-task-badge">This Task</span>}
                {sorted.length > 1 && (
                  <div className="room-slideshow-dots">
                    {sorted.map((a, i) => (
                      <button
                        key={a.ID}
                        className={`room-slideshow-dot${a.TaskID === selectedTask?.id ? ' task-dot' : ''}${i === taskSlideIndex ? ' active' : ''}`}
                        onPointerDown={e => { e.stopPropagation(); setTaskSlideIndex(i); }}
                      />
                    ))}
                  </div>
                )}
                <div className="room-slideshow-meta">
                  {taskSlideIndex + 1} / {sorted.length}{att.Caption ? ` · ${att.Caption}` : ''} · {formatUploadDate(att.UploadedDate)}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default TabletManufacturingPage;